import { useEffect, useMemo, useState, type CSSProperties, type ReactElement } from 'react';
import { v2, type OfficialSpecPackage, type OfficialSpecPackageDetail, type OfficialSpecPackageFile } from '@/api/v2Client';
import { MovableDialog } from '@/components/shared/MovableDialog';
import { Button } from '@/components/shared/Button';
import { ResponsiveTable } from '@/components/shared/ResponsiveTable';
import { formatApiError } from '@/lib/errors';
import { formatDate } from '@/lib/format';
import { specCellStyle, specFilterStyle, specSectionDescriptionStyle, specSectionHeadingStyle, specSelectStyle } from './specFileStyles';

const KIND_LABELS: Readonly<Record<string, string>> = {
  'schema-package': 'Schema package',
  'implementation-package': 'Implementation package',
  'implementation-guide': 'Implementation guide',
  'controlled-vocabulary': 'Controlled vocabulary',
  'validation-criteria': 'Validation criteria',
  'validation-guidance': 'Validation guidance',
  'submission-formats': 'Submission file formats',
  'file-format-specification': 'Accepted file formats',
};

function saveBlob(blob: Blob, fileName: string): void {
  const url = URL.createObjectURL(blob);
  const anchor = document.createElement('a');
  anchor.href = url;
  anchor.download = fileName.split(/[/\\]/).pop() || 'specification';
  anchor.click();
  URL.revokeObjectURL(url);
}

export function OfficialSpecLibrary(): ReactElement {
  const [packages, setPackages] = useState<OfficialSpecPackage[] | null>(null);
  const [error, setError] = useState<string | null>(null);
  const [reloadKey, setReloadKey] = useState(0);
  const [authority, setAuthority] = useState('All');
  const [kind, setKind] = useState('All');
  const [selected, setSelected] = useState<OfficialSpecPackage | null>(null);
  const [downloadingId, setDownloadingId] = useState<string | null>(null);
  const [downloadError, setDownloadError] = useState<string | null>(null);

  useEffect(() => {
    let cancelled = false;
    setPackages(null);
    setError(null);
    v2.specFiles.officialPackages().then((result) => {
      if (!cancelled) setPackages(result.packages);
    }).catch((err: unknown) => {
      if (!cancelled) setError(formatApiError(err, 'The official specification library could not be loaded.'));
    });
    return () => { cancelled = true; };
  }, [reloadKey]);

  const visible = useMemo(() => packages?.filter((item) =>
    (authority === 'All' || item.authority === authority) &&
    (kind === 'All' || item.kind === kind),
  ) ?? [], [packages, authority, kind]);

  const download = async (item: OfficialSpecPackage): Promise<void> => {
    setDownloadingId(item.id);
    setDownloadError(null);
    try {
      const response = await v2.specFiles.downloadOfficialPackage(item.id);
      saveBlob(await response.blob(), item.fileName);
    } catch (err) {
      setDownloadError(formatApiError(err, 'The original file could not be downloaded.'));
    } finally {
      setDownloadingId(null);
    }
  };

  return (
    <section aria-labelledby="official-spec-library-title" style={{ marginBottom: 28 }}>
      <h2 id="official-spec-library-title" style={specSectionHeadingStyle}>Official specification library</h2>
      <p style={specSectionDescriptionStyle}>Original files from ICH and regulators, available to inspect and download.</p>
      {packages === null && error === null && <div role="status">Loading official specifications…</div>}
      {error && <div role="alert" style={errorStyle}>{error} <Button variant="ghost" size="sm" onClick={() => setReloadKey((key) => key + 1)}>Retry library</Button></div>}
      {packages !== null && <>
        <div style={specFilterStyle}>
          <label style={{ fontSize: 12 }}>Authority
            <select aria-label="Library authority" value={authority} onChange={(event) => setAuthority(event.target.value)} style={specSelectStyle}>
              <option value="All">All authorities</option>
              {[...new Set(packages.map((item) => item.authority))].sort().map((value) => <option key={value} value={value}>{value}</option>)}
            </select>
          </label>
          <label style={{ fontSize: 12 }}>File type
            <select aria-label="Library file type" value={kind} onChange={(event) => setKind(event.target.value)} style={specSelectStyle}>
              <option value="All">All file types</option>
              {[...new Set(packages.map((item) => item.kind))].sort().map((value) => <option key={value} value={value}>{KIND_LABELS[value] ?? value}</option>)}
            </select>
          </label>
          <span style={{ color: 'var(--text-muted)', fontSize: 12 }}>{visible.length} of {packages.length} official items</span>
        </div>
        <ResponsiveTable aria-label="Official specification library" data-testid="official-spec-library-table" style={{ width: '100%', borderCollapse: 'collapse' }}>
          <thead>
            <tr style={{ borderBottom: '2px solid var(--border)', textAlign: 'left' }}>
              <th scope="col" style={{ ...specCellStyle, width: '26%' }}>Title</th>
              <th scope="col" style={{ ...specCellStyle, width: '11%' }}>Package version</th>
              <th scope="col" style={{ ...specCellStyle, width: '13%' }}>File availability</th>
              <th scope="col" style={{ ...specCellStyle, width: '16%' }}>Source</th>
              <th scope="col" style={{ ...specCellStyle, width: '18%' }}>AutoeCTD support</th>
              <th scope="col" style={{ ...specCellStyle, width: '16%' }}>Files</th>
            </tr>
          </thead>
          <tbody>
            {visible.length === 0 && <tr><td colSpan={6} style={{ padding: '32px 16px', textAlign: 'center', color: 'var(--text-muted)', fontSize: 13 }}>
              {packages.length === 0 ? 'No official reference files have been imported.' : 'No official items match these library filters.'}
            </td></tr>}
            {visible.map((item) => <tr key={item.id} data-testid={`official-spec-package-${item.id}`}>
              <td style={cellStyle}>
                <strong style={{ fontSize: 14 }}>{item.title}</strong>
                <div style={secondaryTextStyle}>{item.authority} · {KIND_LABELS[item.kind] ?? item.kind}</div>
              </td>
              <td style={cellStyle}>
                <span>Version {item.version}</span>
                <div style={secondaryTextStyle}>eCTD {item.ectdVersion}</div>
              </td>
              <td style={cellStyle}>
                <span>{item.availability === 'available' ? 'File available' : 'File unavailable'}</span>
                <div style={secondaryTextStyle}>{item.fileCount} {item.fileCount === 1 ? 'file' : 'files'}</div>
              </td>
              <td style={cellStyle}>
                <a href={item.sourceUrl} target="_blank" rel="noreferrer">Official source</a>
                <div style={secondaryTextStyle}>Retrieved {formatDate(item.fetchedAt)}</div>
              </td>
              <td style={cellStyle}>
                <span>Library reference</span>
                <details style={secondaryTextStyle}><summary style={{ cursor: 'pointer' }}>Support details</summary><p style={{ margin: '6px 0 0' }}>{item.support.message}</p></details>
              </td>
              <td style={cellStyle}>
                <div style={{ display: 'flex', flexWrap: 'wrap', gap: 6 }}>
                  <Button variant="ghost" size="sm" disabled={item.availability !== 'available'} onClick={() => setSelected(item)}>View files</Button>
                  <Button variant="ghost" size="sm" disabled={item.availability !== 'available' || downloadingId !== null} onClick={() => void download(item)}>
                    {downloadingId === item.id ? 'Downloading…' : 'Download original'}
                  </Button>
                </div>
              </td>
            </tr>)}
          </tbody>
        </ResponsiveTable>
      </>}
      {downloadError && <div role="alert" style={{ ...errorStyle, marginTop: 12 }}>{downloadError}</div>}
      {selected && <OfficialPackageViewer key={selected.id} item={selected} onClose={() => setSelected(null)} />}
    </section>
  );
}

function OfficialPackageViewer({ item, onClose }: { item: OfficialSpecPackage; onClose: () => void }): ReactElement {
  const [detail, setDetail] = useState<OfficialSpecPackageDetail | null>(null);
  const [error, setError] = useState<string | null>(null);
  const [reloadKey, setReloadKey] = useState(0);
  const [query, setQuery] = useState('');
  const [selectedFile, setSelectedFile] = useState<OfficialSpecPackageFile | null>(null);
  const [content, setContent] = useState<string | null>(null);
  const [fileError, setFileError] = useState<string | null>(null);
  const [fileReloadKey, setFileReloadKey] = useState(0);
  const [downloadBusy, setDownloadBusy] = useState(false);
  const [downloadError, setDownloadError] = useState<string | null>(null);

  useEffect(() => {
    let cancelled = false;
    setDetail(null);
    setError(null);
    v2.specFiles.officialPackage(item.id).then((result) => {
      if (!cancelled) setDetail(result);
    }).catch((err: unknown) => {
      if (!cancelled) setError(formatApiError(err, 'The package file list could not be loaded.'));
    });
    return () => { cancelled = true; };
  }, [item.id, reloadKey]);

  useEffect(() => {
    let cancelled = false;
    setContent(null);
    setFileError(null);
    if (selectedFile?.viewable) {
      v2.specFiles.officialPackageFile(item.id, selectedFile.path).then(async (response) => {
        const text = await (await response.blob()).text();
        if (!cancelled) setContent(text);
      }).catch((err: unknown) => {
        if (!cancelled) setFileError(formatApiError(err, 'The selected file could not be loaded.'));
      });
    }
    return () => { cancelled = true; };
  }, [item.id, selectedFile, fileReloadKey]);

  const downloadFile = async (file: OfficialSpecPackageFile): Promise<void> => {
    setDownloadBusy(true);
    setDownloadError(null);
    try {
      const response = await v2.specFiles.officialPackageFile(item.id, file.path);
      saveBlob(await response.blob(), file.path);
    } catch (err) {
      setDownloadError(formatApiError(err, 'The selected file could not be downloaded.'));
    } finally { setDownloadBusy(false); }
  };
  const matchingFiles = detail?.files.filter((file) => file.path.toLowerCase().includes(query.trim().toLowerCase())) ?? [];

  return (
    <MovableDialog persistKey="official-spec-package" title={item.title} ariaLabel={`Files in ${item.title}`} onClose={onClose} width="min(1000px, 94vw)" minSize={{ width: 320, height: 360 }} resizable closeOnBackdrop testId="official-spec-package-viewer">
      <div style={{ display: 'grid', gap: 12, maxHeight: '75vh', overflow: 'auto', fontSize: 13 }}>
        <details style={referenceStyle}><summary>Support details</summary><p role="note" style={{ marginBottom: 0 }}><strong>AutoeCTD support:</strong> {item.support.message}</p></details>
        <div><a href={item.sourceUrl} target="_blank" rel="noreferrer">Official source</a> · Retrieved {formatDate(item.fetchedAt)}</div>
        <details><summary>Original file details</summary><div style={{ overflowWrap: 'anywhere', marginTop: 6 }}>{item.fileName} · {item.byteLength.toLocaleString()} bytes<br />SHA-256: {item.sha256}</div></details>
        {detail === null && error === null && <div role="status">Loading package files…</div>}
        {error && <div role="alert" style={errorStyle}>{error} <Button variant="ghost" size="sm" onClick={() => setReloadKey((key) => key + 1)}>Retry file list</Button></div>}
        {detail && <>
          <label style={{ fontSize: 12 }}>Find a file<input aria-label="Find a package file" type="search" value={query} onChange={(event) => setQuery(event.target.value)} style={{ ...specSelectStyle, minWidth: 220 }} /></label>
          <div style={{ maxHeight: selectedFile ? 180 : 350, overflow: 'auto', border: '1px solid var(--border)', borderRadius: 6 }}>
            {matchingFiles.length === 0 && <p style={{ padding: '0 12px' }}>No files match this search.</p>}
            {matchingFiles.map((file) => <div key={file.path} style={{ padding: '8px 12px', borderBottom: '1px solid var(--border)', display: 'flex', gap: 10, alignItems: 'center', flexWrap: 'wrap' }}>
              <div style={{ flex: '1 1 280px', minWidth: 0, overflowWrap: 'anywhere' }}>{file.path}<div style={{ color: 'var(--text-muted)', fontSize: 11 }}>{file.byteLength.toLocaleString()} bytes</div></div>
              {file.viewable && <Button variant="ghost" size="sm" onClick={() => setSelectedFile(file)} aria-label={`View ${file.path}`}>View</Button>}
              <Button variant="ghost" size="sm" disabled={downloadBusy} onClick={() => void downloadFile(file)} aria-label={`Download ${file.path}`}>Download</Button>
            </div>)}
          </div>
          {downloadError && <div role="alert" style={errorStyle}>{downloadError}</div>}
          {selectedFile && <div>
            <h3 style={{ fontSize: 13, overflowWrap: 'anywhere' }}>{selectedFile.path}</h3>
            {content === null && fileError === null && <div role="status">Loading file contents…</div>}
            {fileError && <div role="alert" style={errorStyle}>{fileError} <Button variant="ghost" size="sm" onClick={() => setFileReloadKey((key) => key + 1)}>Retry contents</Button></div>}
            {content !== null && <pre data-testid="official-spec-file-content" style={{ margin: 0, padding: 12, maxHeight: 340, overflow: 'auto', background: 'var(--surface)', border: '1px solid var(--border)', borderRadius: 6, fontSize: 12, lineHeight: 1.5 }}>{content}</pre>}
            <details style={{ marginTop: 8 }}><summary>File checksum</summary><div style={{ overflowWrap: 'anywhere', marginTop: 6 }}>SHA-256: {selectedFile.sha256}</div></details>
          </div>}
        </>}
      </div>
    </MovableDialog>
  );
}

const referenceStyle: CSSProperties = { margin: 0, padding: '10px 12px', border: '1px solid var(--border)', borderRadius: 6, background: 'var(--surface2)', fontSize: 12, lineHeight: 1.5 };
const cellStyle: CSSProperties = { ...specCellStyle, fontSize: 13 };
const secondaryTextStyle: CSSProperties = { marginTop: 5, color: 'var(--text-muted)', fontSize: 12, lineHeight: 1.5 };
const errorStyle: CSSProperties = { color: 'var(--red)', lineHeight: 1.5 };
